<template>
  <ButtonMenu
    class="inbox-filter"
    placement="bottom"
    :width="160"
    :is-hidden-on-click="false"
  >
    <template #btn>
      <BaseButton
        kind="auxiliary"
        text="Filter"
        :class="{ 'filter-changed': !isFilterDefault }"
      >
        <template #icon>
          <FilterIcon />
        </template>
      </BaseButton>
    </template>
    <template #content>
      <span class="text">Items from:</span>
      <BaseButton
        v-for="choice in filterChoices"
        :key="choice"
        class="choice"
        kind="auxiliary"
        :text="getFilterText(choice)"
        is-expanded
        @click.stop="toggleFilter(choice)"
      >
        <template #icon> <component :is="getFilterIcon(choice)" /> </template
        ><template #right>
          <BaseCheckbox
            :is-checked="getFilterStatus(choice)"
            :is-interactive="false"
            :is-checkbox-tooltip-shown="false"
            tint="red"
            @click="toggleFilter(choice)"
          />
        </template>
      </BaseButton>
    </template>
  </ButtonMenu>
</template>
<script lang="ts">
import { computed, defineComponent, type Component } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import CompleteIcon from "@/components/icons/CompleteIcon.vue";
import CubeIcon from "@/components/icons/CubeIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import FilterIcon from "@/components/icons/FilterIcon.vue";
import NoneIcon from "@/components/icons/NoneIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import ContactsIcon from "@/components/icons/tabs/ContactsIcon.vue";
import * as inject from "@/components/inject";
import {
  defaultTaskParentFilter,
  type TaskParentFilterKey,
} from "@/components/task/types";

// Display order of the filter rows.
const filterChoices: TaskParentFilterKey[] = [
  "event",
  "task",
  "page",
  "person",
  "object",
  "none",
];

const filterTexts: Record<TaskParentFilterKey, string> = {
  event: "Events",
  task: "Tasks",
  page: "Pages",
  person: "People",
  object: "Objects",
  none: "Other",
};

// `ContactsIcon` matches the Contacts menu entry; `CubeIcon` is the generic
// type icon (`TypeIcon`'s fallback) since objects have no single type here.
const filterIcons: Record<TaskParentFilterKey, Component> = {
  event: EventIcon,
  task: CompleteIcon,
  page: PageIcon,
  person: ContactsIcon,
  object: CubeIcon,
  none: NoneIcon,
};

export default defineComponent({
  components: {
    BaseCheckbox,
    FilterIcon,
    ButtonMenu,
    BaseButton,
  },
  setup() {
    const inboxFilter = inject.inboxFilter();
    const toggleFilter = (choice: TaskParentFilterKey) => {
      inboxFilter.value = {
        ...inboxFilter.value,
        [choice]: !inboxFilter.value[choice],
      };
    };
    const getFilterText = (choice: TaskParentFilterKey): string =>
      filterTexts[choice];
    const getFilterIcon = (choice: TaskParentFilterKey): Component =>
      filterIcons[choice];
    const getFilterStatus = (choice: TaskParentFilterKey): boolean =>
      inboxFilter.value[choice];
    const isFilterDefault = computed<boolean>(() =>
      filterChoices.every(
        (choice) =>
          inboxFilter.value[choice] === defaultTaskParentFilter[choice]
      )
    );
    return {
      filterChoices,
      getFilterIcon,
      getFilterStatus,
      getFilterText,
      isFilterDefault,
      toggleFilter,
    };
  },
});
</script>
<style scoped lang="scss">
.inbox-filter {
  button.base-button.none.auxiliary.default.filter-changed {
    @include themed() {
      color: t($secondary);
    }
    :deep(.left) svg .fill,
    &:hover :deep(.left) svg .fill {
      @include themed() {
        fill: t($secondary);
      }
    }
  }
}
button.base-button {
  line-height: 18px;
}
span.text {
  padding: 4px 14px;
  @extend %font-small-regular;
  letter-spacing: 1px;
  text-transform: uppercase;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
